/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";

// Dialog with support for modal={false} for nested dialogs
// When modal={false}, the dialog doesn't trap focus, allowing parent dialogs to remain interactive
// This is essential for nested dialogs (e.g., Client/Jobsite forms opened from Service Form)
const Dialog = ({
  modal = true,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root> & { modal?: boolean }) => (
  <DialogPrimitive.Root {...props} modal={modal} />
);
Dialog.displayName = "Dialog";

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

interface DialogOverlayProps extends React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Overlay
> {
  opaque?: boolean;
}

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  DialogOverlayProps
>(({ className, opaque, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      opaque
        ? "bg-surface-frame" // Fully opaque - hides content behind
        : "bg-[hsl(220_20%_10%_/_0.42)] backdrop-blur-md", // Default semi-transparent
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const dialogSizeClasses = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  "3xl": "max-w-3xl",
  "4xl": "max-w-4xl",
  "5xl": "max-w-5xl",
  "6xl": "max-w-6xl",
  full: "max-w-[95vw] mx-4",
} as const;

export type DialogSize = keyof typeof dialogSizeClasses;

/**
 * Layout mode (CCP-1200 shared responsive overlay contract):
 * - `flow` (default, backward-compatible): the historical grid layout; the
 *   whole content scrolls when `scrollable` is set. Existing consumers are
 *   unchanged.
 * - `panel`: an intentional full-height panel at compact widths — a flex
 *   column bounded to the viewport, so `DialogHeader` / `DialogFooter`
 *   (`shrink-0`) stay pinned while a `DialogBody` scrolls. This is the target
 *   pattern dense forms migrate to instead of hand-rolling
 *   `max-h-[90vh] flex flex-col p-0`.
 */
export type DialogLayout = "flow" | "panel";

interface DialogContentProps extends React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> {
  size?: DialogSize;
  scrollable?: boolean;
  layout?: DialogLayout;
  showCloseButton?: boolean;
  opaqueOverlay?: boolean;
  onInteractOutside?: (event: { detail: { originalEvent: Event } }) => void;
  onPointerDownOutside?: (event: {
    detail: { originalEvent: PointerEvent };
  }) => void;
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    {
      className,
      children,
      size = "lg",
      scrollable,
      layout = "flow",
      showCloseButton = true,
      opaqueOverlay = false,
      onInteractOutside,
      onPointerDownOutside,
      ...props
    },
    ref,
  ) => (
    <DialogPortal>
      <DialogOverlay opaque={opaqueOverlay} />
      <DialogPrimitive.Content
        ref={ref}
        aria-modal="true"
        data-layout={layout}
        className={cn(
          // Positioning + chrome shared by both layouts. At compact widths the
          // dialog anchors to safe insets rather than a centred transform.
          "fixed left-[50%] top-[50%] z-50 w-full translate-x-[-50%] translate-y-[-50%] rounded-[var(--radius-xl)] border border-[var(--border-hairline)] bg-surface-frame text-text-primary shadow-[var(--shadow-frame)] duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] max-sm:inset-x-3 max-sm:inset-y-4 max-sm:mx-auto max-sm:top-auto max-sm:translate-x-0 max-sm:translate-y-0 max-sm:w-auto",
          layout === "panel"
            ? // Bounded flex column; children (header/body/footer) own padding.
              "flex max-h-[calc(100dvh-2rem)] flex-col overflow-hidden p-0 max-sm:h-[calc(100dvh-2rem)] max-sm:max-h-none max-sm:pb-[env(safe-area-inset-bottom)]"
            : "grid gap-4 p-5 max-sm:pb-[calc(1.25rem+env(safe-area-inset-bottom))]",
          dialogSizeClasses[size],
          layout === "flow" && scrollable && "max-h-[90vh] overflow-y-auto",
          className,
        )}
        onInteractOutside={onInteractOutside}
        onPointerDownOutside={onPointerDownOutside}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close className="absolute right-2 top-2 z-10 inline-flex h-11 w-11 items-center justify-center rounded-[var(--radius-md)] text-text-muted opacity-70 transition-[background-color,color,opacity,box-shadow] hover:bg-brand-soft hover:text-text-primary hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-[var(--border-focus)] focus:ring-offset-2 focus:ring-offset-[var(--surface-frame)] disabled:pointer-events-none data-[state=open]:bg-brand-soft">
            <X className="h-5 w-5" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  ),
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

/**
 * Scrolling body region for `layout="panel"` dialogs. Only this region
 * scrolls; the header and footer stay pinned. Owns the standard content
 * padding so panel-mode `DialogContent` can be `p-0`.
 */
const DialogBody = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-5 py-4",
      className,
    )}
    {...props}
  />
);
DialogBody.displayName = "DialogBody";

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      // shrink-0 keeps the header pinned in panel-layout flex columns; it is a
      // no-op in the default grid layout.
      "flex shrink-0 flex-col space-y-1.5 text-center sm:text-left",
      className,
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex shrink-0 flex-col-reverse max-sm:gap-2 max-sm:[&_button]:min-h-[44px] sm:flex-row sm:justify-end sm:space-x-2",
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none text-text-primary",
      className,
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-text-secondary", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
